import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useModalClose } from '../hooks/useModalClose.js'
import { subscribePWAPrompt, triggerPWAInstall } from '../pwaInstall.js'

function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(false)

  useEffect(() => {
    // subscribePWAPrompt calls fn immediately with current value,
    // so we never miss an event that fired before this component mounted.
    return subscribePWAPrompt((prompt) => setCanInstall(Boolean(prompt)))
  }, [])

  const triggerInstall = useCallback(() => triggerPWAInstall(), [])

  return { canInstall, triggerInstall }
}

export default function InstallPWAButton({ isDemo = false }) {
  const { canInstall, triggerInstall } = usePWAInstall()
  const [sheetOpen, setSheetOpen] = useState(false)

  useModalClose(sheetOpen, () => setSheetOpen(false))

  if (!canInstall && !isDemo) return null

  function handleClick() {
    if (isDemo) {
      setSheetOpen(true)
    } else {
      triggerInstall()
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label="Installera Grannäventyret"
        onClick={handleClick}
        className="flex h-9 w-9 items-center justify-center gap-2 rounded-full bg-white/10 text-white transition hover:bg-white/20 md:w-auto md:px-3 dark:bg-white/5 dark:hover:bg-white/10"
      >
        <span aria-hidden="true" className="text-lg leading-none">📲</span>
        <span className="hidden text-sm font-medium md:inline">Installera</span>
      </button>

      {sheetOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center xl:items-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSheetOpen(false)} />
          <div className="relative w-full max-w-sm animate-slide-up rounded-t-3xl bg-[#0B1220] px-5 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-white shadow-2xl xl:rounded-3xl xl:pb-6">
            <div className="mb-3 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-white/15 xl:hidden" />
                <div className="mb-2 text-2xl">📲</div>
                <h3 className="text-lg font-semibold">Installation ej tillgänglig i demo</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  PWA-installation fungerar bara för riktiga konton. Skapa ett konto för att installera Grannäventyret som app på din enhet.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="hidden rounded-full p-1.5 text-white/40 hover:bg-white/10 xl:inline-flex"
                aria-label="Stäng"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 mt-4">
              <Link
                to="/register"
                className="block w-full rounded-2xl bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:bg-indigo-700"
              >
                Skapa konto
              </Link>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-full rounded-2xl bg-white/10 px-4 py-3 text-sm font-semibold text-white/70 transition hover:bg-white/15"
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
